import { useMutation } from '@tanstack/react-query';
import { withOnClickTracking } from 'auto-tracking';
import { useRum } from 'common-rum';
import { useCommonTracking } from 'common-tracking';
import { formatCurrency } from 'common-utils';
import { Button, rem, Spacer, styled, theme } from 'design-system';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import {
  currency,
  getCountryCode,
  isUploadErrorPayload,
  localeList,
  localeListType,
  TUploadErrorCode,
  UploadButtonNew,
} from 'preflight-core';
import React, { useEffect, useRef, useState } from 'react';

import { HeaderHomePage } from '../../../components/header-home-page';
import { AtWorkCardComponent } from '../../../components/homepage/at-work-card-component';
import { Section1HomepageLHS } from '../../../components/homepage/endnote-refworks/section1-homepage-lhs';
import { Section1HomepageLHSPartner } from '../../../components/homepage/endnote-refworks/section1-homepage-lhs-partner';
import { TrustComponent } from '../../../components/homepage/trust-component';
import { BlueBanner } from '../../../components/homepage/v2/blue-banner';
import { ChecklistPopup } from '../../../components/homepage/v2/checklist-popup';
import { ChecklistPreview } from '../../../components/homepage/v2/checklist-preview';
import { TakePreflightSection } from '../../../components/homepage/v2/take-preflight-section';
import { mapJournalToVariant } from '../../../components/journal-variant';
import { NudgingSlider } from '../../../components/nudging-slider';
import {
  Container,
  CustomFontHeaderContainer,
  TopContainer,
} from '../../../components/section';
import { useEventTracking } from '../../../contexts/tracking-context';
import { HeroErrorNew } from '../../../partials/heros/hero-error-new';
import {
  Check,
  getJournal,
  Journal,
  JournalNotFoundError,
} from '../../../resources/journal';
import {
  getAllJournalSlugs,
  JournalSlugs,
} from '../../../resources/journal-slugs';
import { FooterInnerSection } from '../../../sections/footer';
import { TopBannersHome } from '../../../top-banners/top-banner-home';
import { VoucherBannerHome } from '../../../top-banners/voucher-banner-home';
import { defaultPublisherSlugMapping } from '../../../utils/endpoint-v1-default-journal';
import {
  getAllJournalSlugsForEndpoint,
  getAllJournalSlugsForEndpointResType,
  journalObjectType,
} from '../../../utils/get-Journal-list-endpoint-v1';
import { mutateString } from '../../../utils/replace-language';
import { useUploadDocument } from '../../../utils/use-upload-document';
import { useVoucher } from '../../../vouchers/use-voucher';

type Props = {
  defaultJournal: Journal;
};

const JournalInterstitialPage: NextPage<Props> = ({ defaultJournal }) => {
  const { t } = useTranslation(['homepage']);
  const { trackEvent } = useEventTracking();
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const [journal, setJournal] = useState<Journal>(defaultJournal);
  const [isLoadingJournalList, setIsLoadingJournalList] = useState(true);
  const [journalList, setJournalList] =
    useState<getAllJournalSlugsForEndpointResType>({ journals: [] });
  const { trackCommonEvent } = useCommonTracking();
  const [selectedJournal, setSelectedJournal] = useState<journalObjectType>({
    label: journal.label,
    slug: journal.slug,
    id: 0,
  });

  const [showQuickView, setShowQuickView] = useState(false);
  const [errorSection, setErrorSection] = useState('');
  const [errorScreen, setErrorScreen] = useState(false);
  const [otherSectionClicked, setOtherSectionClicked] = useState(false);
  const [uploadStateContinuePdf, setUploadStateContinuePdf] = useState<{
    name: string;
    error?: { json: { errorCode: TUploadErrorCode; errorReason: string } };
    data?: { json: object };
  }>({
    name: 'idle',
  });
  const [currentCountry, setCurrentCountry] = useState<string | undefined>();
  const [tempJournalDetails, setTempJournalDetails] = useState<
    Journal | undefined
  >(journal);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const voucher = useVoucher({
    publisherId: journal.publisher.slug,
    journalId: journal.slug,
  });

  const { resetUpload, upload, uploadState, redirectToD } = useUploadDocument({
    demoId: journal.demoSubmissionId,
    journalId: journal.slug,
    publisherId: journal.publisher.slug,
  });
  const handleLogoClick = () => {
    if (journal.slug === 'refworks') {
      // Open the external URL in a new tab
      window.open('https://refworks.proquest.com/learn-more/', '_blank');
    }
    // Reset the upload state regardless of the journal type
    resetUpload();
  };

  // Pass handleLogoClick as the onLogoClick prop
  <HeaderHomePage
    journal={journal}
    onLogoClick={handleLogoClick}
    aria-label="Header Home Page"
  />;

  useEffect(() => {
    if (uploadState.name === 'error' && otherSectionClicked === false) {
      setErrorScreen(true);
    }
  }, [uploadState]);

  const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState<
    string | undefined
  >(undefined);

  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    'USD',
  );
  const [localCurrencyEnabled, setLocalCurrencyEnabled] =
    useState<boolean>(false);

  const [price, setPrice] = useState<string | undefined>(
    formatCurrency(
      (journal.localPricing?.finalPrice
        ? journal.localPricing?.finalPrice
        : journal.salePriceCents) / 100,
      localeList['USD' as keyof localeListType],
      'USD',
    ),
  );

  useEffect(() => {
    const fetchClientAddress = async () => {
      try {
        const country = await getCountryCode();
        setCurrentCountry(country);
        if (country === 'CN') {
          setSelectedPaymentCurrency(currency.CNY);
        } else if (country === 'IN') {
          setSelectedPaymentCurrency(currency.INR);
        } else if (country === 'JP') {
          setSelectedPaymentCurrency(currency.JPY);
          // } else if (country === 'GB') {
          //   setSelectedPaymentCurrency(currency.GBP);
          // } else if (country === 'KR') {
          //   setSelectedPaymentCurrency(currency.KRW);
          // } else if (
          //   [
          //     'FR',
          //     'DE',
          //     'IT',
          //     'ES',
          //     'NL',
          //     'BE',
          //     'AT',
          //     'IE',
          //     'PT',
          //     'FI',
          //     'GR',
          //     'LU',
          //     'SI',
          //     'SK',
          //     'EE',
          //     'LV',
          //     'LT',
          //     'CY',
          //     'MT',
          //   ].includes(country)
          // ) {
          //   setSelectedPaymentCurrency(currency.EUR);
          // } else if (country === 'SG') {
          //   setSelectedPaymentCurrency(currency.SGD);
          // } else if (country === 'MY') {
          //   setSelectedPaymentCurrency(currency.MYR);
          // } else if (country === 'ID') {
          //   setSelectedPaymentCurrency(currency.IDR);
          // } else if (country === 'BR') {
          //   setSelectedPaymentCurrency(currency.BRL);
        } else {
          setSelectedPaymentCurrency(currency.USD);
        }
      } catch (error) {
        console.error('Failed to fetch client address:', error);
      }
    };

    fetchClientAddress();
  }, []);

  useEffect(() => {
    if (selectedPaymentCurrency && selectedPaymentCurrency !== 'USD') {
      (async () => {
        try {
          const journalDetail = await getJournal({
            journalSlug: selectedJournal?.slug,
            publisherSlug: journal.publisher.slug,
            currency: selectedPaymentCurrency,
          });
          setJournal(journalDetail);
          // if (journalLoader) {
          //   setSelectedJournal({
          //     ...selectedJournal,
          //     label: journalDetail.label,
          //   });
          //   setJournalLoader(false);
          //   setOpenJournal(false);
          // }

          setPrice(
            formatCurrency(
              (journalDetail.localPricing?.finalPrice
                ? journalDetail.localPricing?.finalPrice
                : journalDetail.salePriceCents) / 100,
              localeList[selectedPaymentCurrency as keyof localeListType],
              selectedPaymentCurrency,
            ),
          );
          if (journalDetail.localPricing?.finalPrice) {
            setSelectedCurrency(selectedPaymentCurrency);
            setLocalCurrencyEnabled(true);
          }
        } catch (error) {
          // Handle any errors that may occur during the asynchronous operations.
          console.error(error);
        }
      })();
    }
  }, [selectedPaymentCurrency, selectedJournal.slug]);

  const headerRef = useRef<HTMLDivElement>(null);
  const checklist = journal.checklist.checks;
  const langCheckCount = Object.values(checklist).filter(
    (item) => item.grouping === 'language',
  ).length;
  const techCheckCount = Object.values(checklist).filter(
    (item) => item.grouping !== 'language',
  ).length;

  useEffect(() => {
    const obj = { language: false, technical: false };
    if (langCheckCount) {
      obj['language'] = true;
    }
    if (techCheckCount) {
      obj['technical'] = true;
    }
  }, [langCheckCount, techCheckCount]);

  const [fileSize, setFileSize] = useState<number>();
  const [showVideo, setShowVideo] = useState<boolean>(false);
  function onFileSelected(file: File): void {
    setFileSize(file.size);
    upload(file);
    setOtherSectionClicked(false);
  }
  function onFileSelectedNudge(file: File): void {
    setUploadStateContinuePdf({ name: 'idle' });
    setFileSize(file.size);
    upload(file, true);
  }
  // function onFileSelectedSection2(file: File): void {
  //   setFileSize(file.size);
  //   upload(file);
  //   setOtherSectionClicked(false);
  // }

  function onFileSelectedSection3(file: File): void {
    setFileSize(file.size);
    upload(file);
    setOtherSectionClicked(false);
  }
  const setOpenQuickView = () => {
    setShowQuickView(false);
  };
  const rum = useRum();
  useEffect(() => {
    if (uploadState.name === 'success') {
      trackEvent([
        'Processing_page',
        'Upload_completed',
        'File upload completed',
      ]);

      if (uploadState.data.isCreatedFromDocx) {
        setShowQuickView(true);
      } else {
        redirectToD(uploadState.data);
      }
    }
    if (uploadState.name === 'error') {
      let errorMessage = isUploadErrorPayload(uploadState.error?.json)
        ? uploadState.error?.json?.errorReason ?? ''
        : JSON.stringify(uploadState.error.json) ?? '';

      const maxFileSize20MB = 20000000;

      if (fileSize && fileSize > maxFileSize20MB) {
        errorMessage = ` ${errorMessage}, File Size of uploaded document is ${fileSize} Bytes`;
      }

      rum.pushError(new Error(errorMessage), {
        type: 'Home Page Error',
        context: {
          errorName: 'File processing failed',
        },
      });
      trackEvent([
        'Report generation status',
        'Processing failed',
        'File processing failed',
        {
          ReportStatus: 'NA',
          GenerationStatus: 'failed to generate',
          ReasonForFailure: errorMessage,
          CountryCode: currentCountry ?? '',
        },
      ]);
    }
  }, [uploadState.name]);

  const handleContinueWithPdfCallback = (newBool: boolean) => {
    RedirectNewPdf(newBool);
  };

  const RedirectNewPdf = (newBool: boolean) => {
    if (uploadState.name === 'success') {
      redirectToD(uploadState.data, newBool, setUploadStateContinuePdf);
    }
  };

  const { mutateAsync: fetchJournalsForEndpointApi } = useMutation({
    mutationFn: getAllJournalSlugsForEndpoint,
    onSuccess: async (data, variables) => {
      if (variables.journalSlug) {
        trackCommonEvent({
          type: 'search_paperpal',
          search_query: variables.journalSlug ?? '',
          input_to_track: 'search_for_journal_name',
          search_result: data.journals.length > 0 ? 'success' : 'failure',
        });
      }

      // Remove those journal which have -ea in end
      const filteredJournals: getAllJournalSlugsForEndpointResType = {
        journals: data.journals.filter(
          (journal) => !journal.slug.endsWith('-ea'),
        ),
      };

      setJournalList(filteredJournals);
      setIsLoadingJournalList(false);
    },
    onError() {
      setIsLoadingJournalList(false);
      setJournalList({ journals: [] });

      const errorMessage = 'Error while fetching Journals';
      rum.pushError(new Error(errorMessage), {
        type: 'Journal list error',
        context: {
          errorName: 'Error while fetching journals',
        },
      });
    },
  });
  const [languageCheckLists, setLanguageCheckLists] = useState<Check[]>([]);
  const [technicalCheckLists, setTechnicalCheckLists] = useState<Check[]>([]);

  useEffect(() => {
    const technicalChecks = Object.values(journal.checklist.checks).filter(
      (check) => check.grouping !== 'language',
    );
    setTechnicalCheckLists(technicalChecks); // Store the full check objects for technical checks

    const languageCheckList = Object.values(journal.checklist.checks).filter(
      (check) => check.grouping === 'language',
    );
    setLanguageCheckLists(languageCheckList); // Store the full check objects for language checks
  }, [journal.checklist]);
  const mappedJournal = mapJournalToVariant(journal.publisher.slug);

  //End note fusion

  return (
    <TopContainer>
      <div>
        {showQuickView && (
          <NudgingSlider
            uploadState={uploadState}
            uploadStateContinuePdf={uploadStateContinuePdf}
            onFileSelected={onFileSelectedNudge}
            open={showQuickView}
            setOpen={setOpenQuickView}
            onContinueWithPdfClick={handleContinueWithPdfCallback}
            // preflightNudgeAccepted={handleContinueWithWordCallback}
          />
        )}

        <StickyHeader>
          <TopBannersHome
            submissionState={null}
            hasMacros={false}
            showServiceUnavaliable={false}
            journal={journal}
            voucher={voucher.data ? voucher.data : null}
          />
          <div ref={headerRef}>
            <HeaderHomePage
              journal={journal}
              onLogoClick={handleLogoClick}
              aria-label="Header Home Page"
              pageType="publisher"
            />
          </div>
        </StickyHeader>

        <SectionNewV1
          size="sm2"
          style={{
            paddingBottom: '60px',
            background: theme.colors.white.value,
          }}
        >
          <Container data-test-id="voucher-banner">
            {voucher.data ? (
              <div>
                <VoucherText>
                  {voucher.data ? (
                    <VoucherBannerHome voucher={voucher.data} />
                  ) : undefined}
                </VoucherText>
                <Spacer height={32} />
              </div>
            ) : (
              ''
            )}

            <FullWidthWrapper
              ref={scrollTargetRef}
              style={{ display: 'flex', gap: '20px' }}
            >
              {errorSection === 'Section_1' && errorScreen && !showQuickView ? (
                <LeftReadyToSubmitSection>
                  <HeroErrorNew
                    onFileSelected={onFileSelected}
                    uploadState={uploadState}
                    showSubmission={false}
                    submissionUrl={''}
                  />
                </LeftReadyToSubmitSection>
              ) : journal.publisher.slug === 'refworks' ||
                journal.publisher.slug === 'endnote' ||
                journal.publisher.slug === 'endnote-fusion' ? (
                <Section1HomepageLHS
                  journal={journal}
                  languageCheckLists={languageCheckLists}
                  technicalCheckLists={technicalCheckLists}
                  onFileSelected={(f) => {
                    onFileSelected(f);
                  }}
                  uploadState={uploadState}
                  showQuickView={showQuickView}
                  setErrorScreen={setErrorScreen}
                  errorSection={errorSection}
                  setErrorSection={setErrorSection}
                  setOtherSectionClicked={setOtherSectionClicked}
                  techCheckCount={techCheckCount}
                  langCheckCount={langCheckCount}
                  fetchJournalsForEndpointApi={fetchJournalsForEndpointApi}
                  journalList={journalList}
                  publisherSlug={journal.publisher.slug}
                  selectedJournal={selectedJournal}
                  setSelectedJournal={setSelectedJournal}
                  isLoadingJournalList={isLoadingJournalList}
                  setIsLoadingJournalList={setIsLoadingJournalList}
                  tempJournalDetails={tempJournalDetails}
                  setTempJournalDetails={(data) => setTempJournalDetails(data)}
                  selectedPaymentCurrency={selectedPaymentCurrency}
                  buttonDisabled={
                    uploadState.name === 'loading' ||
                    uploadState.name === 'success'
                  }
                />
              ) : (
                <Section1HomepageLHSPartner
                  journal={journal}
                  languageCheckLists={languageCheckLists}
                  technicalCheckLists={technicalCheckLists}
                  onFileSelected={(f) => {
                    onFileSelected(f);
                  }}
                  uploadState={uploadState}
                  showQuickView={showQuickView}
                  setErrorScreen={setErrorScreen}
                  errorSection={errorSection}
                  setErrorSection={setErrorSection}
                  setOtherSectionClicked={setOtherSectionClicked}
                  techCheckCount={techCheckCount}
                  langCheckCount={langCheckCount}
                  fetchJournalsForEndpointApi={fetchJournalsForEndpointApi}
                  journalList={journalList}
                  publisherSlug={journal.publisher.slug}
                  selectedJournal={selectedJournal}
                  setSelectedJournal={setSelectedJournal}
                  isLoadingJournalList={isLoadingJournalList}
                  setIsLoadingJournalList={setIsLoadingJournalList}
                  tempJournalDetails={tempJournalDetails}
                  setTempJournalDetails={(data) => setTempJournalDetails(data)}
                  selectedPaymentCurrency={selectedPaymentCurrency}
                  buttonDisabled={
                    uploadState.name === 'loading' ||
                    uploadState.name === 'success'
                  }
                />
              )}
              <RightGradientSection>
                <RightReadyToSubmitSection>
                  {journal.publisher.slug === 'refworks' ||
                  journal.publisher.slug === 'endnote' ||
                  journal.publisher.slug === 'endnote-fusion' ? (
                    <div
                      style={{ position: 'relative', marginTop: '20px' }}
                      data-test-id="hero-video-play-trigger"
                      onClick={() => setShowVideo(true)}
                    >
                      <Image
                        width={560}
                        height={521}
                        src={'/endnote-lp.png'}
                        alt={journal.label}
                      />
                      <VideoComponent data-test-id="hero-video-play-button">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        <VideoCompText>
                          {t(
                            'section1_v2.watch_how_it_works',
                            'Watch how it works',
                          )}
                        </VideoCompText>
                      </VideoComponent>
                    </div>
                  ) : (
                    <div style={{ position: 'relative', marginTop: '20px' }}>
                      <Image
                        width={560}
                        height={521}
                        src={'/hero-main-file.svg'}
                        alt={`${journal.label} - Partner`}
                      />
                    </div>
                  )}

                  {showVideo &&
                    (journal.publisher.slug === 'refworks' ||
                      journal.publisher.slug === 'endnote' ||
                      journal.publisher.slug === 'endnote-fusion') && (
                      <>
                        <VideoComp>
                          <ShowVideo>
                            <ShowVideoIframe
                              style={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                              }}
                            >
                              <iframe
                                width="100%"
                                height="100%"
                                src={`https://cdn.paperpal.com/assets/videos/${
                                  journal.publisher.slug === 'refworks'
                                    ? 'preflight/refworks-lp-v1'
                                    : journal.publisher.slug === 'endnote'
                                    ? 'preflight/endnote-lp-v1'
                                    : journal.publisher.slug ===
                                      'endnote-fusion'
                                    ? 'preflight/endnote-lp-v1'
                                    : ''
                                }.mp4`}
                                style={{ border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />

                              <VideoButton
                                data-test-id="hero-video-close-button"
                                onClick={() => setShowVideo(false)}
                              >
                                <Image
                                  width={24}
                                  height={24}
                                  src={'/white-tick.svg'}
                                  alt={'cross-button'}
                                />
                              </VideoButton>
                            </ShowVideoIframe>
                          </ShowVideo>
                        </VideoComp>
                      </>
                    )}
                </RightReadyToSubmitSection>
              </RightGradientSection>
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>

        <SectionNewV1 bgTheme="lightBlue3" size={'xsm'}>
          <Container>
            <FullWidthWrapper>
              <BlueBanner />
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>
        <SectionNewV1 bgTheme="white" size={'xsm2'}>
          <Container>
            <FullWidthWrapper>
              <RightGradientSection2>
                <TakePreflightSection
                  price={price}
                  selectedPaymentCurrency={selectedCurrency}
                  localCurrencyEnabled={localCurrencyEnabled}
                  langCheckCount={langCheckCount}
                  techCheckCount={techCheckCount}
                  journal={journal}
                />
              </RightGradientSection2>
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>

        <SectionNewV1 bgTheme="lightBlue1">
          <Container>
            <FullWidthWrapper
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 1,
              }}
            >
              <SectionTitle
                style={{
                  textAlign: 'center',
                  color: theme.colors.blue10.value,
                  marginBottom: '10px',
                }}
              >
                {t('section2_v1.submission_readiness_checklist')}
              </SectionTitle>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeading
                  style={{
                    textAlign: 'center',
                    width: '560px',
                    marginBottom: '50px',
                  }}
                >
                  {t('section2_v1.main_heading1')}
                </MainHeading>
              </CustomFontHeaderContainer>
              <Spacer height={30} />
            </FullWidthWrapper>
            <ChecklistPreview journal={journal} />
            <Spacer height={40} />
            <FullWidthWrapper
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {errorSection === 'Section_2' && errorScreen ? (
                <LeftReadyToSubmitSectionError style={{ flex: '0 0 auto' }}>
                  <SectionButtonStyling>
                    <HeroErrorNew
                      onFileSelected={onFileSelectedSection3}
                      uploadState={uploadState}
                      align="center"
                    />
                  </SectionButtonStyling>
                </LeftReadyToSubmitSectionError>
              ) : (
                <SectionButtonStyling>
                  <UploadButtonNew
                    id="Get-Your-Manuscript-Checked"
                    data-test-id="Get-Your-Manuscript-Checked"
                    aria-label="Get-Your-Manuscript-Checked"
                    disabled={
                      uploadState.name === 'loading' ||
                      uploadState.name === 'success'
                    }
                    onClick={() => {
                      trackEvent([
                        'Manuscript upload intent',
                        'Upload click',
                        'Get your manuscript checked',
                        {
                          Section: 'Section 2',
                        },
                      ]);
                      setOtherSectionClicked(false);
                      if (errorSection !== 'Section_2' && errorSection !== '') {
                        setOtherSectionClicked(true);
                        setErrorScreen(false);
                      }
                      setErrorSection('Section_2');
                    }}
                    onFileSelected={onFileSelectedSection3}
                    size="small"
                    isIcon={true}
                    text={t('section2_v1.get_your_manuscript_checked')}
                    isHover={true}
                    optionalLanguageText={{
                      processing: t('common.processing'),
                    }}
                    trackingProperties={{
                      CTALabel: 'Start Assessment For FREE',
                      elementName: 'manuscript_upload',
                      sectionName: 'section_2',
                    }}
                    style={{
                      color: 'rgba(0, 84, 241, 1)',
                      border: `1px solid rgba(0, 84, 241, 1)`,
                      backgroundColor: 'transparent',
                      borderColor: 'rgba(0, 84, 241, 1)',
                      fontSize: '20px',
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      height: '70px',
                      width: '100px',
                      boxShadow: 'none',
                    }}
                    // isBlueBack={true}
                  />
                </SectionButtonStyling>
              )}
            </FullWidthWrapper>
          </Container>
          <Spacer height={10} />
        </SectionNewV1>

        <SectionNewV1 bgTheme="white" style={{ marginBottom: -50 }}>
          <Container>
            <AtWork>
              <AtWorkLeft>
                {errorSection === 'Section_3' && errorScreen ? (
                  <LeftReadyToSubmitSectionError style={{ flex: '0 0 auto' }}>
                    <HeroErrorNew
                      onFileSelected={onFileSelectedSection3}
                      uploadState={uploadState}
                    />
                  </LeftReadyToSubmitSectionError>
                ) : (
                  <>
                    <SectionTitle style={{ justifyContent: 'left' }}>
                      {t('section4_v1.preflight_at_work')}
                    </SectionTitle>
                    <CustomFontHeaderContainer journal={mappedJournal}>
                      <MainHeading
                        style={{ marginBottom: '15px', marginTop: '4px' }}
                      >
                        {t('section4_v1.preflight_ready_to_submit')}
                      </MainHeading>
                    </CustomFontHeaderContainer>
                    <div
                      style={{
                        fontSize: '20px',
                        color: 'rgba(5, 30, 87, 1)',
                        lineHeight: '32px',
                        fontWeight: 400,
                      }}
                    >
                      {mutateString(t('section4_v1.left_description_else'), {
                        '{techCheckCount}': `${techCheckCount}`,
                        '{langCheckCount}': `${langCheckCount}`,
                      })}
                    </div>
                    <FreeAssesmentBtnWrap>
                      <UploadButtonNew
                        id="Start-Free-Assesment"
                        data-test-id="Start-Free-Assesment"
                        aria-label="Start-Free-Assesment"
                        disabled={
                          uploadState.name === 'loading' ||
                          uploadState.name === 'success'
                        }
                        onClick={() => {
                          trackEvent([
                            'Manuscript upload intent',
                            'Upload click',
                            'Start free assessment',
                            {
                              Section: 'Section 3',
                            },
                          ]);
                          setOtherSectionClicked(false);
                          if (
                            errorSection !== 'Section_3' &&
                            errorSection !== ''
                          ) {
                            setOtherSectionClicked(true);
                            setErrorScreen(false);
                          }
                          setErrorSection('Section_3');
                        }}
                        onFileSelected={onFileSelectedSection3}
                        size="small"
                        isIcon={true}
                        text={t('section4_v1.start_free_assessment')}
                        isHover={true}
                        optionalLanguageText={{
                          processing: t('common.processing'),
                        }}
                        trackingProperties={{
                          CTALabel: 'Start free assessment',
                          elementName: 'manuscript_upload',
                          sectionName: 'section_3',
                        }}
                        style={{
                          color: 'rgba(0, 84, 241, 1)',
                          border: `1px solid rgba(0, 84, 241, 1)`,
                          backgroundColor: theme.colors.white,
                          borderColor: 'rgba(0, 84, 241, 1)',
                          fontSize: '20px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          height: '70px',
                          width: '340px',
                          boxShadow: 'none',
                        }}
                        // isBlueBack={true}
                      />
                    </FreeAssesmentBtnWrap>
                    <div style={{ paddingLeft: '50px', paddingTop: '30px' }}>
                      <Image
                        src="/v1-landing-page/section4-smile.svg"
                        alt="At Work"
                        style={{ marginTop: '20px' }}
                        width={220}
                        height={40}
                      />
                    </div>
                  </>
                )}
              </AtWorkLeft>
              <AtWorkRight>
                <AtWorkCardComponent
                  langCheckCount={langCheckCount}
                  techCheckCount={techCheckCount}
                  voucher={voucher.data ? voucher.data : null}
                  price={price}
                  version="v1"
                />
              </AtWorkRight>
            </AtWork>
          </Container>
        </SectionNewV1>

        <SectionNewV1 bgTheme="darkBlue">
          <RightGradientSection3>
            <Container>
              <MainHeadingCommitment2
                style={{
                  marginBottom: '4px',
                  textAlign: 'center',
                }}
              >
                {t('section5_V1.our_commitment', 'OUR COMMITMENT')}
                {''}
              </MainHeadingCommitment2>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeadingCommitment
                  style={{
                    marginBottom: '40px',
                    textAlign: 'center',
                    color: 'white',
                  }}
                >
                  {t('section5_V1.why_trust_paperpal')}
                </MainHeadingCommitment>
              </CustomFontHeaderContainer>
              <TrustContainer>
                <TrustComponent v1={true} />
              </TrustContainer>

              <div
                style={{
                  marginTop: '20px',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                {' '}
                <FullWidthWrapper
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {errorSection === 'Section_4' && errorScreen ? (
                    <LeftReadyToSubmitSectionError style={{ flex: '0 0 auto' }}>
                      <SectionButtonStyling>
                        <HeroErrorNew
                          onFileSelected={onFileSelectedSection3}
                          uploadState={uploadState}
                          align="center"
                        />
                      </SectionButtonStyling>
                    </LeftReadyToSubmitSectionError>
                  ) : (
                    <SectionButtonStyling>
                      <UploadButtonNew
                        id="Get-Your-Manuscript-Checked"
                        data-test-id="Get-Your-Manuscript-Checked"
                        aria-label="Get-Your-Manuscript-Checked"
                        disabled={
                          uploadState.name === 'loading' ||
                          uploadState.name === 'success'
                        }
                        onClick={() => {
                          trackEvent([
                            'Manuscript upload intent',
                            'Upload click',
                            'Get your manuscript checked',
                            {
                              Section: 'Section 4',
                            },
                          ]);
                          setOtherSectionClicked(false);
                          if (
                            errorSection !== 'Section_4' &&
                            errorSection !== ''
                          ) {
                            setOtherSectionClicked(true);
                            setErrorScreen(false);
                          }
                          setErrorSection('Section_4');
                        }}
                        onFileSelected={onFileSelectedSection3}
                        size="small"
                        isBlueBack={true}
                        text={t('section4_v1.start_free_assessment')}
                        optionalLanguageText={{
                          processing: t('common.processing'),
                        }}
                        trackingProperties={{
                          CTALabel: 'Start free assessment',
                          elementName: 'manuscript_upload',
                          sectionName: 'section_4',
                        }}
                      />
                    </SectionButtonStyling>
                  )}
                </FullWidthWrapper>
              </div>
            </Container>
          </RightGradientSection3>
        </SectionNewV1>
        <SectionNewV1
          bgTheme="grayDark"
          style={{ paddingTop: '0px', paddingBottom: '0px' }}
        >
          <FooterInnerSection journal={journal} onLogoClick={handleLogoClick} />
        </SectionNewV1>
      </div>
      <ChecklistPopup
        journal={journal}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      />
    </TopContainer>
  );
};

export default JournalInterstitialPage;

export const getStaticPaths: GetStaticPaths<JournalSlugs> = async () => {
  const allJournalSlugs = await getAllJournalSlugs();
  const paths = allJournalSlugs.map(({ journalSlug, publisherSlug }) => ({
    params: {
      journalSlug,
      publisherSlug,
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props> = async (context) => {
  const journalSlugs = context.params;
  const publisherSlug = journalSlugs?.publisherSlug as string;
  const { locale } = context;
  if (!journalSlugs) {
    console.error('Unable to get static props without journal slugs');
    return { notFound: true };
  }

  let journalSlug: JournalSlugs = {
    journalSlug: defaultPublisherSlugMapping?.[publisherSlug]
      ?.default as string,
    publisherSlug,
  };

  if (
    publisherSlug === 'gjd-journal-finder' ||
    publisherSlug === 'editage-cn'
  ) {
    console.error(
      `Unable to get static props without journal slugs ${journalSlug.publisherSlug}`,
    );
    return { notFound: true };
  }

  if (!journalSlug.journalSlug) {
    const allJournals = await getAllJournalSlugsForEndpoint({ publisherSlug });
    const filteredJournals = allJournals.journals.filter(
      (journal) => !journal.slug.endsWith('-ea'),
    );
    const publisherJournalSlug = filteredJournals[0]?.slug ?? '';
    journalSlug = {
      journalSlug: publisherJournalSlug,
      publisherSlug,
    };
  }

  if (!journalSlug.journalSlug) {
    console.error(
      `Unable to get static props without journal slug for publisher ${journalSlug.publisherSlug}`,
    );
    return { notFound: true };
  }

  let defaultJournal: Journal;

  try {
    defaultJournal = await getJournal(journalSlug);
  } catch (error) {
    if (JournalNotFoundError.safeParse(error).success) {
      return { notFound: true };
    }

    throw error;
  }

  let translations = {};

  if (locale) {
    translations = await serverSideTranslations(locale, [
      'homepage',
      'common',
      'error',
    ]);
  }

  return {
    props: {
      ...translations,
      defaultJournal,
    },
    revalidate: 5,
  };
};

export const LeftReadyToSubmitSection = styled('div', {
  flex: 1,
  // paddingTop: '35px',
});
export const LeftReadyToSubmitSectionError = styled('div', {
  flex: 1,
});
export const RightReadyToSubmitSection = styled('div', {
  width: '530px',
  height: '531px',
});

export const StickyHeader = styled('div', {
  position: 'sticky',
  top: 0,
  zIndex: 2,
});

export const BlueSmallHeading = styled('div', {
  fontSize: '18px',
  color: theme.colors.blue11,
  fontWeight: 700,
  lineHeight: '23.4px',
  marginBottom: '20px',
});

export const VoucherText = styled('div', {
  fontSize: '18px',
  color: theme.colors.blue11,
  fontWeight: 700,
  lineHeight: '23.4px',
  borderRadius: '10px',
});

export const Chip = withOnClickTracking(
  styled('div', {
    backgroundColor: theme.colors.blue4,
    borderRadius: '30px',
    padding: '9px',
    paddingLeft: '15px',
    paddingRight: '15px',
    display: 'inline-flex',
    lineHeight: '15px',
    fontSize: '12px',
    fontWeight: '600',
    color: theme.colors.blue10,
  }),
);

export const FullWidthWrapper = styled('div', {
  width: '100%',
  marginBottom: -40,
});

export const ButtonWrap = styled('div', {
  minWidth: '300px',
  '& > Button': {
    width: '100%',
    height: '63px',
  },
  '& > Button:hover': {
    backgroundColor: theme.colors.blue11,
  },
});

export const FreeAssesmentBtnWrap = styled('div', {
  marginTop: '40px',
  display: 'inline-block',
  minWidth: '250px',
  '& > Button': {
    width: '100%',
    height: '63px',
  },
  '& > Button:hover': {
    backgroundColor: theme.colors.blue11,
  },
});

export const SectionTitle = styled('span', {
  display: 'flex',
  fontSize: '20px',
  color: theme.colors.blue10,
  fontWeight: 700,
  justifyContent: 'center',
  justifyItems: 'center',
  textTransform: 'uppercase',
});

export const MainHeading = styled('span', {
  fontSize: '30px',
  lineHeight: '40px',
  fontWeight: '700',
  display: 'block',
  color: 'rgba(5, 30, 87, 1)',
  // textTransform: 'capitalize',
});

const MainHeadingCommitment = styled('span', {
  fontSize: '30px',
  lineHeight: '46px',
  fontWeight: '700',
  display: 'block',
  color: 'rgba(255, 255, 255, 1)',
});

// const MainHeadingCommitment2 = styled('span', {
//   fontSize: '20px',

//   fontWeight: '700',
//   display: 'block',

//   fontFamily: 'IBM Plex Sans',
//   color: 'rgba(94, 150, 239, 1)',
//   textTransform: 'uppercase',
// });

export const BlueSmallHeading1 = styled('div', {
  fontSize: '14px',
  color: theme.colors.blue11,
  fontWeight: 700,
  lineHeight: '23.4px',
  marginBottom: '16px',
});

const TrustContainer = styled('div', {
  display: 'flex',
});

export const SectionButtonStyling = styled('div', {
  '>button': {
    minWidth: '348px',
    height: '63px',
  },
  '& > button:hover': {
    backgroundColor: theme.colors.blue11,
  },
});

const AtWork = styled('div', {
  display: 'flex',
  gap: '72px',
});

const AtWorkLeft = styled('div', {
  width: '450px',
});

const AtWorkRight = styled('div', {
  flex: 1,
});

const RightGradientSection = styled('div', {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '40px',
    left: '40px',
    right: '40px',
    bottom: '40px',
    background:
      'linear-gradient(rgb(255 216 221) 0%, rgb(236, 193, 79) 40.5%, hsl(140deg 67.72% 82.55%) 84%, rgb(6 139 222) 100%)',
    opacity: '20%',
    zIndex: 0,
    transform: 'rotate(-280deg)',
    pointerEvents: 'none',
    filter: 'blur(70px)',
  },
});
const RightGradientSection2 = styled('div', {
  position: 'relative',
  display: 'flex',
  marginTop: '14px',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0px 0px 0px 700px',
    background: 'linear-gradient(rgb(255 164 175) 20%, rgb(197 233 255) 80%)',
    opacity: '0.2',
    zIndex: 0,
    transform: 'rotate(-242deg)',
    pointerEvents: 'none',
    filter: 'blur(80px)',
    width: '400px',
  },
});
const RightGradientSection3 = styled('div', {
  position: 'relative',
  display: 'flex',
  marginTop: '14px',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0px 0px 0px auto',
    background: 'linear-gradient(rgb(255 18 45) 20%, rgb(20 165 255) 80%)',
    opacity: '0.2',
    zIndex: 0,
    transform: 'rotate(-243deg)',
    pointerEvents: 'none',
    filter: 'blur(80px)',
    width: '447px',
    marginRight: '189px',
  },
});

const ShowVideo = styled('div', {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '756px',
  height: '425px',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  borderRadius: '10px',

  boxShadow: '0px 12.14px 24.29px 0px #23170542',
});
const ShowVideoIframe = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
});

export const SectionNewV1 = styled('div', {
  paddingTop: rem(48),
  paddingBottom: rem(80),
  background: theme.colors.white,
  paddingLeft: rem(32),
  paddingRight: rem(32),
  minWidth: rem(1234),
  variants: {
    size: {
      xsm: {
        paddingTop: '30px',
        paddingBottom: '20px',
      },
      xsm2: {
        paddingTop: '30px',
        paddingBottom: '70px',
      },
      sm: {
        paddingTop: rem(40),
        paddingBottom: rem(40),
      },
      sm2: {
        paddingBottom: rem(40),
      },
      md: {
        paddingTop: rem(60),
        paddingBottom: rem(60),
      },
      lg: {
        paddingTop: rem(70),
        paddingBottom: rem(70),
      },
    },
    bgTheme: {
      gray: {
        background: theme.colors.gray2,
      },
      grayDark: {
        background: theme.colors.gray5,
      },
      yellow: {
        background:
          'linear-gradient(to top, rgba(249, 240, 209, 1), rgba(249, 240, 209, 0.5), rgba(249, 240, 209, 0))',
      },
      darkBlue: {
        background: 'rgba(5, 30, 87, 1)',
      },
      // eslint-disable-next-line custom/no-hardcoded-colors
      lightBlue3: {
        background: '#052161',
      },
      lightBlue1: {
        background: 'rgba(237, 243, 255, 1)',
      },
      lightBlue2: {
        background: 'rgba(225, 235, 255, 1)',
      },
      white: {
        background: 'rgba(255, 255, 255, 1)',
      },
    },
  },
});

// eslint-disable-next-line custom/no-hardcoded-colors
const VideoComponent = styled('div', {
  position: 'absolute',
  top: '50%',
  left: '50%',
  height: '44px',
  transform: 'translate(-50%, -50%)',
  backgroundColor: '#052161',
  borderRadius: 40,
  padding: '10px',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.colors.blue10,
  },
});

const MainHeadingCommitment2 = styled('span', {
  fontSize: '20px',

  fontWeight: '700',
  display: 'block',

  color: 'rgba(94, 150, 239, 1)',
  textTransform: 'uppercase',
});

const VideoCompText = withOnClickTracking(
  styled('div', {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    width: '132px',
    color: 'white',
  }),
);
const VideoButton = styled(Button, {
  position: 'absolute',
  top: '-50px',
  right: '-50px',
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
});

const VideoComp = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9998,

  backdropFilter: 'blur(7px)',
});
